"use client";

/**
 * Playback result → URL for the player. HLS: install CDN cookies first; CloudFront checks playlist and every segment.
 */

import type { api } from "@v1/backend/convex/_generated/api";
import type { FunctionReturnType } from "convex/server";
import {
  canUseCdnCookies,
  cdnCookiePath,
  playlistUrlForMint,
} from "./clip-cdn-cookie-scope";

/** Derived from the action's return type; a hand-written mirror drifts silently. */
export type ClipPlaybackResult = NonNullable<
  FunctionReturnType<typeof api.clips.playback.getClipPlaybackUrl>
>;

function setAccessCookie(
  name: string,
  value: string,
  domain: string,
  path: string,
  maxAgeSeconds: number,
): void {
  document.cookie = `${name}=${value}; Domain=${domain}; Path=${path}; Max-Age=${maxAgeSeconds}; Secure; SameSite=Lax`;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

/** Hosts of all embedding pages. Unknown → "" → site check fails → MP4. */
function ancestorHosts(): string[] {
  if (window.self === window.top) return [];
  if ("ancestorOrigins" in window.location) {
    return Array.from(window.location.ancestorOrigins, hostOf);
  }
  // Firefox lacks ancestorOrigins; referrer is the embedding page.
  return [hostOf(document.referrer)];
}

/** Installs playback credentials if needed, returns URL to load. Null = nothing to play. */
export function applyClipPlayback(
  result: ClipPlaybackResult | null | undefined,
): string | null {
  if (!result) return null;
  if (result.kind === "file") return result.url;
  if (
    !canUseCdnCookies(
      result.cookie_domain,
      window.location.hostname,
      ancestorHosts(),
    )
  ) {
    return result.file_url;
  }
  // Cookie outliving its signature → 403 indistinguishable from real denial.
  const maxAge = Math.max(
    0,
    Math.floor((result.expires_at - Date.now()) / 1000),
  );
  const path = cdnCookiePath(result.url);
  setAccessCookie(
    "CloudFront-Policy",
    result.policy,
    result.cookie_domain,
    path,
    maxAge,
  );
  setAccessCookie(
    "CloudFront-Signature",
    result.signature,
    result.cookie_domain,
    path,
    maxAge,
  );
  setAccessCookie(
    "CloudFront-Key-Pair-Id",
    result.key_pair_id,
    result.cookie_domain,
    path,
    maxAge,
  );
  return playlistUrlForMint(result.url, result.expires_at);
}
